import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';
import {
  FormControl,
  FormLabel,
  Select,
  Typography,
  Flex,
  Spinner,
} from '@tq/ui-components/dist/components';
import { Form, FormProvider } from '@tq/ui-components/dist/components/forms-engine';
import { tqc } from '@tq/ui-components/dist/system';

import { useDfiAnnualReports, API_QUERY_KEYS } from 'api';
import { annualDfiReportYearState } from 'store';
import { StatusIndicator } from './StatusIndicator';

type ReportDateProps = {
  onSubmit?: () => void;
};

export const ReportFiscalYear: React.FC<ReportDateProps> = ({ onSubmit }) => {
  const { t } = useTranslation('report');
  const queryClient = useQueryClient();

  const [annualDfiReportYear, setAnnualDfiReportYear] = useRecoilState(
    annualDfiReportYearState
  );

  const [financialYear, setFinancialYear] = useState<number>(annualDfiReportYear);

  const { data: response } = useDfiAnnualReports();

  const handleSubmit = () => {
    setAnnualDfiReportYear(financialYear);
    queryClient.invalidateQueries({ queryKey: [API_QUERY_KEYS.getDfiAnnualReport] });
    onSubmit?.();
  };

  const handleChange = (selectedOption: string) => {
    setFinancialYear(+selectedOption);
  };

  return response ? (
    <FormProvider mode='onChange'>
      <Form id='report-dates-form' onValidSubmit={handleSubmit}>
        <FormControl width='100%'>
          <FormLabel id='fiscal_year_form_label' htmlFor='annual_dfi_report_select'>
            {t('financial_year', 'Financial year')}:
          </FormLabel>
          <Select
            onChange={handleChange}
            aria-labelledby='fiscal_year_form_label'
            inputId='annual_dfi_report_select'
            value={financialYear}
            w='425px'
            aria-label='Select financial year'
          >
            {response.listDfiAnnualReportItems?.map((report) => (
              <option key={report.reportYear} value={report.reportYear}>
                <Flex justifyContent='space-between' gap={4}>
                  <Typography>
                    <tqc.p>
                      {`${t('1_april', '1st April')} ${report.reportYear} ${t(
                        '31_march',
                        ' to 31st March'
                      )} ${report.reportYear + 1}`}
                    </tqc.p>
                  </Typography>
                  <StatusIndicator complete={!!report.dateCompleted} />
                </Flex>
              </option>
            ))}
          </Select>
        </FormControl>
      </Form>
    </FormProvider>
  ) : (
    <Spinner />
  );
};
